// Calendar-only arithmetic avoids browser timezone and daylight-saving offsets.
const DAY = 86_400_000;
export function berlinDay(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const value = (type) => Number(parts.find((part) => part.type === type).value);
  return new Date(Date.UTC(value('year'), value('month') - 1, value('day')));
}

function easterSunday(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451), n = h + l - 7 * m + 114;
  return new Date(Date.UTC(year, Math.floor(n / 31) - 1, n % 31 + 1));
}

export function isHoliday(day, production = false) {
  const key = `${day.getUTCMonth() + 1}-${day.getUTCDate()}`;
  if (['1-1', '5-1', '10-3', '12-25', '12-26'].includes(key)) return true;
  // The production location is Berlin; destination-specific holidays are unknown.
  if (production && key === '3-8') return true;
  const fromEaster = Math.round((day - easterSunday(day.getUTCFullYear())) / DAY);
  return [-2, 1, 39, 50].includes(fromEaster);
}

function checkedDays(value, fallback) {
  const number = Number(value);
  return Number.isInteger(number) && number >= 1 && number <= 40 ? number : fallback;
}

export function addWorkingDays(start, count, { production = false, saturday = false } = {}) {
  const result = new Date(start);
  if (!Number.isFinite(result.getTime())) throw new TypeError('Invalid start date');
  let left = checkedDays(count, 1);
  while (left > 0) {
    result.setUTCDate(result.getUTCDate() + 1);
    const weekday = result.getUTCDay();
    if (weekday !== 0 && (weekday !== 6 || (!production && saturday)) && !isHoliday(result, production)) left--;
  }
  return result;
}

export function productionStart(now = new Date(), cutoffHour = 12) {
  const day = berlinDay(now);
  const hour = Number(new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin', hour: '2-digit', hourCycle: 'h23',
  }).format(now));
  const suppliedCutoff = cutoffHour === null || cutoffHour === '' ? 12 : Number(cutoffHour);
  const cutoff = Number.isInteger(suppliedCutoff) && suppliedCutoff >= 0 && suppliedCutoff <= 23 ? suppliedCutoff : 12;
  if (day.getUTCDay() === 0 || day.getUTCDay() === 6 || isHoliday(day, true) || hour >= cutoff) {
    return addWorkingDays(day, 1, { production: true });
  }
  return day;
}

export function deliveryRange({ now = new Date(), express = false, productionMin = 3, productionMax = 3, expressDays = 1, transitMin = 2, transitMax = 3, saturday = false, cutoffHour = 12 } = {}) {
  const start = productionStart(now, cutoffHour);
  const minProduction = express ? checkedDays(expressDays, 1) : checkedDays(productionMin, 3);
  const maxProduction = express ? minProduction : Math.max(minProduction, checkedDays(productionMax, 3));
  const minTransit = checkedDays(transitMin, 2);
  const maxTransit = Math.max(minTransit, checkedDays(transitMax, 3));
  return {
    from: addWorkingDays(addWorkingDays(start, minProduction, { production: true }), minTransit, { saturday }),
    to: addWorkingDays(addWorkingDays(start, maxProduction, { production: true }), maxTransit, { saturday }),
  };
}

export function expressSelected(options, index, onValue) {
  const values = Array.isArray(onValue) ? onValue : onValue ? [onValue] : [];
  return Number.isInteger(index) && index >= 0 && values.includes(options[index]);
}

export function formatRange(range, locale = 'de-DE') {
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC', day: '2-digit', month: '2-digit', weekday: 'short',
    ...(range.from.getUTCFullYear() !== range.to.getUTCFullYear() ? { year: 'numeric' } : {}),
  });
  return `${formatter.format(range.from)} – ${formatter.format(range.to)}`;
}

export function formatArrivalDate(date, locale = 'de-DE', today = berlinDay()) {
  const differentYear = date.getUTCFullYear() !== today.getUTCFullYear();
  if (/^de(?:-|$)/i.test(locale)) {
    const weekday = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long' }).format(date);
    return `${weekday}, ${date.getUTCDate()}.${date.getUTCMonth() + 1}.${differentYear ? date.getUTCFullYear() : ''}`;
  }
  return new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'short',
    ...(differentYear ? { year: 'numeric' } : {}),
  }).format(date);
}

if (typeof window !== 'undefined' && !customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]');
      this.onChange = () => this.update();
      this.section?.addEventListener('change', this.onChange);
      window.addEventListener('pageshow', this.onChange);
      document.addEventListener('visibilitychange', this.onChange);
      this.timer = window.setInterval(this.onChange, 60_000);
      this.update();
    }

    disconnectedCallback() {
      this.section?.removeEventListener('change', this.onChange);
      window.removeEventListener('pageshow', this.onChange);
      document.removeEventListener('visibilitychange', this.onChange);
      window.clearInterval(this.timer);
    }

    update() {
      const selected = this.section?.querySelector('[data-variant-select]')?.selectedOptions[0];
      this.hidden = selected ? selected.dataset.available !== 'true' : this.dataset.available !== 'true';
      if (this.hidden) return;
      let options = [];
      try { options = JSON.parse(selected?.dataset.options || '[]'); } catch { /* retain the standard estimate */ }
      let expressValues;
      try { expressValues = JSON.parse(this.dataset.expressValues || 'null'); } catch { /* use the standalone express option */ }
      const express = expressSelected(options, Number(this.dataset.expressIndex), Array.isArray(expressValues) && expressValues.length ? expressValues : this.dataset.expressOn);
      const range = deliveryRange({
        express,
        productionMin: this.dataset.productionMin,
        productionMax: this.dataset.productionMax,
        expressDays: this.dataset.expressDays,
        transitMin: this.dataset.transitMin,
        transitMax: this.dataset.transitMax,
        saturday: this.dataset.saturday === 'true',
        cutoffHour: this.dataset.cutoffHour,
      });
      const output = this.querySelector('[data-delivery-date]');
      const text = range.from.getTime() === range.to.getTime()
        ? formatArrivalDate(range.from, this.dataset.locale || 'de-DE')
        : formatRange(range, this.dataset.locale || 'de-DE');
      if (output && output.textContent !== text) output.textContent = text;
      const detail = this.querySelector('[data-delivery-duration]');
      const duration = express ? this.dataset.expressCopy : this.dataset.standardCopy;
      if (detail && detail.textContent !== duration) detail.textContent = duration;
      this.dataset.mode = express ? 'express' : 'standard';
      this.dataset.arrivalDate = range.from.toISOString().slice(0, 10);
      this.dataset.dateFrom = range.from.toISOString().slice(0, 10);
      this.dataset.dateTo = range.to.toISOString().slice(0, 10);
    }
  });
}
